import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { 
    Add as AddIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon,
    KeyboardArrowUp as UpIcon,
    KeyboardArrowDown as DownIcon
} from '@mui/icons-material';
import { useSeries } from '../../hooks/useSeries';
import { useContentList } from '../../hooks/useContentList';
import SectionForm from '../../Components/SectionForm/SectionForm';
import './Sections.css';

const Sections = ({ 
    sections = [], 
    loading = false, 
    error = null, 
    createSection, 
    updateSection, 
    deleteSection, 
    sortSections,
    refreshSections,
    openSectionsForm,
    setOpenSectionsForm
}) => {
    const [openForm, setOpenForm] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sectionToDelete, setSectionToDelete] = useState(null);

    const { series, refreshSeries } = useSeries();
    const { contents, refreshContents } = useContentList();

    useEffect(() => {
        refreshSeries();
        refreshContents();
    }, []);

    useEffect(() => {
        if (openSectionsForm) {
            handleOpenForm();
            setOpenSectionsForm(false);
        }
    }, [openSectionsForm]);

    const handleOpenForm = (section = null) => {
        setSelectedSection(section);
        setOpenForm(true);
    };

    const handleCloseForm = () => {
        setSelectedSection(null);
        setOpenForm(false);
    };

    const handleSubmit = async (formData) => {
        if (selectedSection) {
            // Para edição, remover o campo position para não afetar a ordem da seção
            const { position, ...editData } = formData;
            await updateSection(selectedSection.id, editData);
        } else {
            // Converter posição para índice (posição 1 = índice 0, posição 2 = índice 1, etc.)
            const requestedPosition = parseInt(formData.position);
            
            // Calcular o índice correto
            const targetIndex = requestedPosition - 1;
            
            // Criar a seção com o índice correto
            const newFormData = {
                ...formData,
                index: targetIndex
            };
            
            await createSection(newFormData);
        }
        handleCloseForm();
    };

    const handleOpenDeleteDialog = (section) => {
        setSectionToDelete(section);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setSectionToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleDelete = async () => {
        if (sectionToDelete) {
            await deleteSection(sectionToDelete.id);
            handleCloseDeleteDialog();
        }
    };

    const moveSection = async (section, direction) => {
        const sortedSections = [...sections].sort((a, b) => a.index - b.index);
        const currentPosition = sortedSections.findIndex(s => s.id === section.id);
        
        if (direction === 'up' && currentPosition > 0) {
            // Trocar com a seção acima
            const sectionAbove = sortedSections[currentPosition - 1];
            
            // Preparar dados para o endpoint sort - usar posições únicas
            const sectionsData = sortedSections.map((s, index) => {
                if (s.id === section.id) {
                    return { section_id: s.id, section_index: currentPosition - 1 };
                } else if (s.id === sectionAbove.id) {
                    return { section_id: s.id, section_index: currentPosition };
                } else {
                    return { section_id: s.id, section_index: index };
                }
            });
            
            await sortSections(sectionsData);
        } else if (direction === 'down' && currentPosition < sortedSections.length - 1) {
            // Trocar com a seção abaixo
            const sectionBelow = sortedSections[currentPosition + 1];
            
            // Preparar dados para o endpoint sort - usar posições únicas
            const sectionsData = sortedSections.map((s, index) => {
                if (s.id === section.id) {
                    return { section_id: s.id, section_index: currentPosition + 1 };
                } else if (s.id === sectionBelow.id) {
                    return { section_id: s.id, section_index: currentPosition };
                } else {
                    return { section_id: s.id, section_index: index };
                }
            });
            
            await sortSections(sectionsData);
        }
    };

    const handleMoveUp = (section) => {
        moveSection(section, 'up');
    };

    const handleMoveDown = (section) => {
        moveSection(section, 'down');
    };

    if (loading) {
        return <Typography>Carregando...</Typography>;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    const sortedSections = [...sections].sort((a, b) => a.index - b.index);

    return (
        <Box className="sections-page">
            <TableContainer component={Paper} className="sections-table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Ordem</TableCell>
                            <TableCell>Título</TableCell>
                            <TableCell>Keywords</TableCell>
                            <TableCell>Visual Format</TableCell>
                            <TableCell>Itens</TableCell>
                            <TableCell align="center">Reordenar</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedSections.map((section, index) => (
                            <TableRow key={section.id}>
                                <TableCell>{section.index + 1}</TableCell>
                                <TableCell>{section.title}</TableCell>
                                <TableCell>
                                    {section.keywords.map((keyword, index) => (
                                        <span key={index} className="keyword-tag">
                                            {keyword}
                                        </span>
                                    ))}
                                </TableCell>
                                <TableCell>
                                    <span className="format-tag">
                                        {section.exhibitionType || 'landscape'}
                                    </span>
                                </TableCell>
                                <TableCell>{section.chapters.length} itens</TableCell>
                                <TableCell align="center">
                                    <IconButton
                                        onClick={() => handleMoveUp(section)}
                                        disabled={index === 0}
                                        className="reorder-button"
                                        style={{ 
                                            color: index === 0 ? '#666' : '#fff',
                                            marginRight: 4
                                        }}
                                        title="Mover para cima"
                                    >
                                        <UpIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleMoveDown(section)}
                                        disabled={index === sortedSections.length - 1}
                                        className="reorder-button"
                                        style={{ 
                                            color: index === sortedSections.length - 1 ? '#666' : '#fff',
                                            marginLeft: 4
                                        }}
                                        title="Mover para baixo"
                                    >
                                        <DownIcon />
                                    </IconButton>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        onClick={() => handleOpenForm(section)}
                                        style={{ color: '#fff' }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleOpenDeleteDialog(section)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <SectionForm
                open={openForm}
                onClose={handleCloseForm}
                onSubmit={handleSubmit}
                initialData={selectedSection || { position: sections.length + 1 }}
                availableContents={contents}
                availableSeries={series}
                sections={sections}
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                className="delete-dialog"
            >
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir a seção "{sectionToDelete?.title}"?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Sections; 